import { Plus, Minus, Clock, ChevronRight, CheckCircle2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, memo } from 'react';
import { useCart } from '@/contexts/CartContext';
import { SubService } from '@/lib/types';
import { cn } from '@/lib/utils';

interface Props {
  sub: SubService;
  serviceId: string;
  serviceName: string;
  serviceSlug: string;
  index: number;
}

const SubServiceItem = memo(({ sub, serviceId, serviceName, serviceSlug, index }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const { items, addItem, updateQuantity } = useCart();
  const navigate = useNavigate();
  const qty = items.find(i => i.subServiceId === sub.id)?.quantity || 0;

  // 🔒 SACRED: Correct reverse-discount formula — selling price is NEVER modified
  // MRP = Price / (1 - Discount/100)
  const mrp = sub.mrpOverride 
    || (sub.mrpMarkupPercentage && sub.mrpMarkupPercentage > 0 && sub.mrpMarkupPercentage < 100
      ? Math.round(sub.price / (1 - sub.mrpMarkupPercentage / 100))
      : null);
  const discountPercent = (mrp && mrp > sub.price) ? Math.round(((mrp - sub.price) / mrp) * 100) : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
    >
      <div className="rounded-2xl bg-card shadow-card border border-border/50 hover:shadow-elevated hover:border-primary/20 transition-all duration-300 overflow-hidden mb-3">
        <div className="p-4 md:p-5">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-sm md:text-base">{sub.name}</h4>
                {discountPercent && discountPercent > 0 && (
                  <span className="text-[9px] md:text-[10px] font-bold text-success bg-success/5 border border-success/20 px-1.5 py-0.5 rounded-md whitespace-nowrap">
                    SAVE {discountPercent}%
                  </span>
                )}
              </div>
              <p className="text-xs md:text-sm text-muted-foreground mt-0.5 line-clamp-2">{sub.description}</p>
            </div>
            <div className="flex flex-col items-end ml-3 shrink-0 min-w-[80px]">
              <div className="flex items-baseline gap-1.5">
                <span className="font-bold text-primary text-sm md:text-lg">₹{sub.price}</span>
                {mrp && mrp > sub.price && (
                  <span className="text-[10px] md:text-xs text-muted-foreground line-through decoration-muted-foreground/50">
                    ₹{mrp}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Improved layout row: Ensures "See Details" and "Add/Book" buttons don't overlap on small screens */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-3 gap-3">
            <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
              <span className="flex items-center gap-1 text-[11px] md:text-xs text-muted-foreground whitespace-nowrap">
                <Clock className="h-3 w-3" />
                {sub.duration}
              </span>
              <Link to={`/service/${serviceSlug}/detail/${sub.id}`} className="text-primary font-bold hover:underline inline-flex items-center gap-0.5 text-[13px] md:text-sm whitespace-nowrap">
                See Details <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            
            <div className="flex items-center gap-2 w-full sm:flex-1">
              {qty === 0 ? (
                <button
                  onClick={() => addItem({
                    serviceId: serviceId,
                    subServiceId: sub.id,
                    name: serviceName,
                    subServiceName: sub.name,
                    price: sub.price,
                    duration: sub.duration,
                    imageUrl: '',
                  })}
                  className="flex-1 h-12 rounded-xl font-bold border-2 border-primary text-primary bg-transparent text-sm hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-1.5 shrink-0 transform hover:scale-[1.03] active:scale-95 shadow-sm"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              ) : (
                <div className="flex-1 flex items-center justify-between bg-secondary rounded-xl p-1 shadow-sm shrink-0 h-12 px-2">
                  <button onClick={() => updateQuantity(sub.id, -1)} className="h-8 w-8 rounded-lg bg-card text-muted-foreground flex items-center justify-center hover:bg-background shadow-sm transition-colors transform active:scale-90">
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="font-bold text-sm min-w-4 text-center">{qty}</span>
                  <button onClick={() => updateQuantity(sub.id, 1)} className="h-8 w-8 rounded-lg bg-card text-primary flex items-center justify-center hover:bg-background shadow-sm transition-colors transform active:scale-90">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              )}
              <button
                onClick={() => navigate(`/book/${serviceSlug}/${sub.id}`)}
                className="flex-1 h-12 rounded-xl font-bold bg-primary text-primary-foreground text-sm shadow-primary-glow hover:bg-primary/90 hover:shadow-lg transition-all duration-300 shrink-0 transform hover:scale-[1.03] active:scale-95"
              >
                Book Now
              </button>
            </div>
          </div>
          </div>

        {sub.detailedDescription && (
          <div className="border-t border-border/50">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between px-4 md:px-5 py-2.5 text-xs font-medium text-primary hover:bg-secondary/50 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" />
                What's Included
              </span>
              <ChevronRight className={cn("h-3.5 w-3.5 transition-transform duration-200", expanded ? "rotate-90" : "")} />
            </button>
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 md:px-5 pb-4 text-xs text-muted-foreground whitespace-pre-line leading-relaxed">
                    {sub.detailedDescription}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
});

SubServiceItem.displayName = 'SubServiceItem';

export default SubServiceItem;
